import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, type PostValue } from './client'

/**
 * Settings -> RustDesk, calea noua.
 *
 * Perechile vechi, din `application/controllers/settings/Rustdesk.php`:
 *
 *   index             -> GET  /settings/rustdesk
 *   save              -> POST /settings/rustdesk
 *   credential_save   -> POST /settings/rustdesk/accounts
 *   credential_delete -> POST /settings/rustdesk/accounts/{id}/remove
 *   switch_tool       -> POST /settings/rustdesk/tool
 *
 * In portal, ecranul era o BUCATA adusa prin ajax in coaja setarilor de firma
 * (`settings/companies`), si dupa fiecare salvare macheta cerea fila din nou
 * (`$tab.load(...)`), fiindca butoanele de comutare depind de ce s-a salvat.
 * Aici e ecran propriu, si fiecare scriere raspunde cu ECRANUL INTREG - deci
 * nu mai e nevoie de a doua cerere.
 */

export type RustdeskTab = {
  id: string
  label: string
  icon: string
  href: string
  right: string
  group: string
}

/** Configurarea, cu valorile implicite deja puse de server. */
export type RustdeskConfig = {
  hbbs_host: string
  hbbs_port: number
  hbbr_host: string
  hbbr_port: number
  server_key: string
  recording_days: number
  allow_file_transfer: boolean
  require_recording: boolean
  allow_stealth: boolean
  require_consent: boolean
  /**
   * CEC 64: statia mai tine o parola permanenta, raportata serverului (numai
   * portalul vechi o mai citeste)? Fals = numai parole de unica folosinta.
   * Lipsa = server vechi.
   */
  permanent_password?: boolean
}

/** Un cont de administrare. Parola NU vine niciodata, sub niciun nume. */
export type RustdeskAccount = {
  id: number
  label: string
  username: string
  /** `local` = secretul e la noi, cifrat (Defuse); `hashicorp` = calea din HashiCorp Vault. */
  provider: string
  vault?: string
  createdAt: string
}

/** O statie care si-a raportat identitatea. Nimic de aici nu se scrie de mana. */
export type RustdeskDevice = {
  id: number
  deviceId: number
  name: string
  rustdeskId: string
  ip: string
  lastReportedAt: string
}

export type RustdeskTool = 'screenconnect' | 'rustdesk'

export type RustdeskShell = {
  pageTitle: string
  moduleUrl: string
  tabs: RustdeskTab[]
  /** `settings.my_company.edit`. */
  canWrite: boolean
  /** Fals = furnizorul n-are inca niciun rand scris. */
  exists: boolean
  settings: RustdeskConfig
  activeTool: RustdeskTool
  usable: boolean
  /** Gol cand configurarea tine; altfel, motivul, cu vorbele serverului. */
  whyNotUsable: string
  adminAccounts: RustdeskAccount[]
  /** Fals = instalarea n-are cheie de cifrare, deci un cont nou nu se poate scrie. */
  canStorePasswords: boolean
  /** Seifurile configurate pe instalare (`local`, `hashicorp`). */
  vaults?: string[]
  devices: RustdeskDevice[]
}

export const rustdeskShellQuery = queryOptions({
  queryKey: ['settings', 'rustdesk'],
  queryFn: () => apiGet<RustdeskShell>('/settings/rustdesk'),
})

/** Fiecare scriere raspunde cu ecranul intreg. */
export function saveRustdesk(fields: Record<string, PostValue>) {
  return apiPost<RustdeskShell & { saved: boolean }>('/settings/rustdesk', fields)
}

export function addRustdeskAccount(fields: Record<string, PostValue>) {
  return apiPost<RustdeskShell & { saved: boolean }>('/settings/rustdesk/accounts', fields)
}

export function removeRustdeskAccount(id: number) {
  return apiPost<RustdeskShell & { removed: boolean }>(`/settings/rustdesk/accounts/${id}/remove`, {})
}

export function switchRustdeskTool(tool: RustdeskTool) {
  return apiPost<RustdeskShell & { switched: boolean }>('/settings/rustdesk/tool', { tool })
}
